import dayjs from '@plugins/local-dayjs'

import { FieldTypeMap, RelationTypeMap } from '../../../../common/common'
import { getUid } from '../../../../plugins/utils'
import { isNumericCompareLogic, isEmptyCheckLogic, EMPTY_CHECK_SELECT_CONTENT_VALUE } from './applyFieldRuleOptions'

const normalizeDictItem = (item) => {
  if (!item || typeof item !== 'object') {
    return null
  }

  const label = item.dictLabel ?? item.label
  const value = item.dictValue ?? item.value

  if (label == null && value == null) {
    return null
  }

  return { label, value }
}

const buildSelectContentFromItems = (fieldCode, selectContentItems, selectContentName) => {
  const fromItems = (selectContentItems || [])
    .map(normalizeDictItem)
    .filter(Boolean)
    .map(item => ({
      fieldKey: fieldCode,
      label: item.label,
      value: item.value
    }))

  if (fromItems.length) {
    return fromItems
  }

  const names = Array.isArray(selectContentName)
    ? selectContentName
    : (selectContentName != null && selectContentName !== '' ? [selectContentName] : [])

  return names
    .filter(name => name != null && name !== '')
    .map(label => ({
      fieldKey: fieldCode,
      label,
      value: String(label)
    }))
}

export const getRuleSelectContent = (rule) => {
  const formData = rule.formData
  if (!formData?.fieldCode) {
    return []
  }

  if (isEmptyCheckLogic(formData.contentLogicType)) {
    return [{
      fieldKey: formData.fieldCode,
      value: EMPTY_CHECK_SELECT_CONTENT_VALUE
    }]
  }

  switch (String(formData.fieldType)) {
    case FieldTypeMap.input:
      if (formData.selectContentName == null || formData.selectContentName === '') {
        return []
      }
      return String(formData.selectContentName).replaceAll('、', '|').split('|').map(value => {
        return {
          fieldKey: formData.fieldCode,
          value
        }
      })
    case FieldTypeMap.number:
      if (formData.selectContentName == null || formData.selectContentName === '') {
        return []
      }
      return [{
        fieldKey: formData.fieldCode,
        value: String(formData.selectContentName)
      }]
    case FieldTypeMap.select:
      return buildSelectContentFromItems(
        formData.fieldCode,
        formData.selectContentItems,
        formData.selectContentName
      )
    case FieldTypeMap.dateRangePicker:
      if (formData.selectContentName?.length) {
        return [{
          fieldKey: formData.fieldCode,
          value: formData.selectContentName.map(date => {
            return date ? dayjs(date).format('YYYY-MM-DD') : ''
          }).join(',')
        }]
      }
      return []
    case FieldTypeMap.cascader:
      if (formData.selectContentItems?.length) {
        const cascaderItems = formData.selectContentItems.filter(item => item?.value != null || item?.label != null)
        if (!cascaderItems.length) {
          return []
        }
        return [{
          fieldKey: formData.fieldCode,
          value: JSON.stringify(cascaderItems.map((item, index) => {
            return {
              code: item.value,
              name: item.label,
              type: ['province', 'city', 'district'][index] || 'district'
            }
          }))
        }]
      }
      return []
    default:
      if (formData.selectContentName != null && formData.selectContentName !== '') {
        return [{
          fieldKey: formData.fieldCode,
          value: String(formData.selectContentName)
        }]
      }
      return []
  }
}

const transformConditionRows = (rows) => {
  return (rows || [])
    .filter(row => row.formData?.fieldCode)
    .map((row, index) => {
      return {
        fieldCode: row.formData.fieldCode,
        fieldName: row.formData.fieldName,
        fieldType: row.formData.fieldType,
        fieldJoinSelect: row.formData.fieldJoinSelect,
        contentLogicType: row.formData.contentLogicType,
        ruleLogicType: index === 0
          ? RelationTypeMap.or
          : (row.formData.ruleLogicType ?? RelationTypeMap.or),
        selectContent: getRuleSelectContent({ formData: row.formData }),
        childRule: []
      }
    })
}

const transformSubRuleGroups = (groups) => {
  return (groups || [])
    .map(group => {
      const childRule = transformConditionRows(group.children)
      if (!childRule.length) {
        return null
      }
      return {
        ruleLogicType: RelationTypeMap.or,
        childRule
      }
    })
    .filter(Boolean)
}

export const transformRuleTreeToApi = (list) => {
  return (list || []).filter(rule => rule.formData?.fieldCode).map(rule => {
    return {
      fieldCode: rule.formData.fieldCode,
      fieldName: rule.formData.fieldName,
      fieldType: rule.formData.fieldType,
      fieldJoinSelect: rule.formData.fieldJoinSelect,
      ruleLogicType: RelationTypeMap.or,
      childRule: transformSubRuleGroups(rule.children)
    }
  })
}

export const buildApplyFieldListPayload = (applyFieldList) => {
  return (applyFieldList || []).map((field, index) => {
    return {
      fieldCode: field.fieldCode,
      fieldName: field.fieldName,
      fieldType: field.fieldType,
      fieldJoinSelect: field.fieldJoinSelect,
      fieldSort: field.fieldSort ?? index
    }
  })
}

export const buildSaveApplyFieldRulePayload = ({ examId, applyFieldList, ruleTreeChildren }) => {
  return {
    examId: String(examId),
    applyFieldList: buildApplyFieldListPayload(applyFieldList),
    applyFieldRuleList: transformRuleTreeToApi(ruleTreeChildren)
  }
}

const isSelectContentNameEmpty = (formData) => {
  const { selectContentName } = formData

  if (selectContentName == null || selectContentName === '') {
    return true
  }

  if (Array.isArray(selectContentName)) {
    return selectContentName.length === 0
      || selectContentName.every(item => item == null || item === '')
  }

  return false
}

export const isRuleContentFilled = (formData) => {
  if (!formData?.fieldCode) {
    return false
  }

  if (isEmptyCheckLogic(formData.contentLogicType)) {
    return true
  }

  if (formData.contentLogicType == null) {
    return false
  }

  return getRuleSelectContent({ formData }).length > 0 && !isSelectContentNameEmpty(formData)
}

export const validateApplyFieldRuleTree = (ruleTreeChildren) => {
  const errors = []

  ;(ruleTreeChildren || []).forEach(mainRule => {
    const mainFieldName = mainRule.formData?.fieldName || '规则字段'

    if (!mainRule.children?.length) {
      errors.push(`「${mainFieldName}」：请至少添加一条子规则`)
      return
    }

    ;(mainRule.children || []).forEach((group, groupIndex) => {
      ;(group.children || []).forEach((row, rowIndex) => {
        const formData = row.formData
        if (!formData?.fieldCode) {
          return
        }

        const conditionLabel = `${mainFieldName} / 子规则${groupIndex + 1} / 条件${rowIndex + 1}`

        if (!formData.fieldName) {
          errors.push(`${conditionLabel}：请选择字段`)
          return
        }

        if (formData.contentLogicType == null) {
          errors.push(`${conditionLabel}「${formData.fieldName}」：请选择内容逻辑`)
          return
        }

        if (!isRuleContentFilled(formData)) {
          errors.push(`${conditionLabel}「${formData.fieldName}」：请填写规则内容`)
        }
      })
    })
  })

  return errors
}

const parseSelectContentName = (item) => {
  if (isEmptyCheckLogic(item.contentLogicType)) {
    const placeholderValue = item.selectContent?.[0]?.value
    if (placeholderValue === EMPTY_CHECK_SELECT_CONTENT_VALUE || placeholderValue == null) {
      return undefined
    }
  }

  const selectContent = item.selectContent || []

  switch (String(item.fieldType)) {
    case FieldTypeMap.input:
      return selectContent
        .filter(data => data?.value != null && data.value !== '')
        .map(data => data.value)
        .join('、')
    case FieldTypeMap.number:
      if (isNumericCompareLogic(item.contentLogicType)) {
        const value = selectContent[0]?.value
        return value == null || value === '' ? undefined : Number(value)
      }
      return selectContent[0]?.value ?? ''
    case FieldTypeMap.select:
      return selectContent
        .filter(data => data?.label != null)
        .map(data => data.label)
    case FieldTypeMap.dateRangePicker:
      if (selectContent[0]?.value) {
        const dates = selectContent[0].value.split(',').map(date => {
          return date ? dayjs(date) : null
        })
        if (dates.every(item => item == null || !item.isValid())) {
          return undefined
        }
        return dates
      }
      return undefined
    case FieldTypeMap.cascader:
      if (selectContent[0]?.value) {
        try {
          return JSON.parse(selectContent[0].value).map(data => data.name)
        } catch {
          return []
        }
      }
      return []
    default:
      return selectContent[0]?.value ?? ''
  }
}

const parseSelectContentItems = (item) => {
  if (isEmptyCheckLogic(item.contentLogicType)) {
    return undefined
  }

  const selectContent = item.selectContent || []

  switch (String(item.fieldType)) {
    case FieldTypeMap.select:
      return selectContent
        .filter(data => data?.label != null || data?.value != null)
        .map(data => {
          return {
            dictLabel: data.label,
            dictValue: data.value
          }
        })
    case FieldTypeMap.cascader:
      if (selectContent[0]?.value) {
        try {
          return JSON.parse(selectContent[0].value).map(data => {
            return {
              ...data,
              label: data.name,
              value: data.code
            }
          })
        } catch {
          return []
        }
      }
      return []
    default:
      return undefined
  }
}

const parseConditionRow = (item) => {
  return {
    code: getUid(),
    formData: {
      fieldCode: item.fieldCode,
      fieldName: item.fieldName,
      fieldType: item.fieldType,
      fieldJoinSelect: item.fieldJoinSelect,
      contentLogicType: item.contentLogicType,
      ruleLogicType: item.ruleLogicType,
      selectContentName: parseSelectContentName(item),
      selectContentItems: parseSelectContentItems(item)
    },
    children: []
  }
}

const parseSubRuleGroup = (group) => {
  return {
    code: getUid(),
    formData: {
      ruleLogicType: group.ruleLogicType ?? RelationTypeMap.or
    },
    children: (group.childRule || []).map(parseConditionRow)
  }
}

const parseMainRule = (rule) => {
  return {
    code: getUid(),
    parent: {
      code: 'root',
      label: rule.fieldName
    },
    formData: {
      fieldCode: rule.fieldCode,
      fieldName: rule.fieldName,
      fieldType: rule.fieldType,
      fieldJoinSelect: rule.fieldJoinSelect,
      ruleLogicType: rule.ruleLogicType ?? RelationTypeMap.or
    },
    children: (rule.childRule || []).map(parseSubRuleGroup)
  }
}

export const parseApplyFieldRuleListToTree = (list) => {
  return (list || []).map(parseMainRule)
}
